import React, { createContext, useContext, useState, useCallback } from 'react';
import { Progress } from '@/components/ui/progress';
import { FileDown, Loader2 } from 'lucide-react';

type ExportStatus = 'preparing' | 'capturing' | 'optimizing' | 'assembling' | 'complete' | 'error';

interface ExportProgressState {
  visible: boolean;
  title: string;
  message: string;
  percent: number;
  status: ExportStatus;
}

interface ExportProgressContextValue {
  show: (title?: string) => void;
  update: (message: string, percent?: number, status?: ExportStatus) => void;
  complete: (message?: string) => void;
  error: (message?: string) => void;
  dismiss: () => void;
}

const ExportProgressContext = createContext<ExportProgressContextValue | null>(null);

export function useExportProgress() {
  const ctx = useContext(ExportProgressContext);
  if (!ctx) throw new Error('useExportProgress must be used within ExportProgressProvider');
  return ctx;
}

const defaultState: ExportProgressState = {
  visible: false,
  title: 'Generating PDF',
  message: 'Preparing document…',
  percent: 0,
  status: 'preparing',
};

export function ExportProgressProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ExportProgressState>(defaultState);

  const show = useCallback((title = 'Generating PDF') => {
    setState({ visible: true, title, message: 'Preparing document…', percent: 0, status: 'preparing' });
  }, []);

  const update = useCallback((message: string, percent?: number, status?: ExportStatus) => {
    setState(prev => ({
      ...prev,
      message,
      percent: percent ?? prev.percent,
      status: status ?? prev.status,
    }));
  }, []);

  const complete = useCallback((message = 'Export complete!') => {
    setState(prev => ({ ...prev, message, percent: 100, status: 'complete' }));
    setTimeout(() => setState(defaultState), 2000);
  }, []);

  const error = useCallback((message = 'Export failed. Please try again.') => {
    setState(prev => ({ ...prev, message, status: 'error' }));
    setTimeout(() => setState(defaultState), 3000);
  }, []);

  const dismiss = useCallback(() => setState(defaultState), []);

  return (
    <ExportProgressContext.Provider value={{ show, update, complete, error, dismiss }}>
      {children}
      {state.visible && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4 space-y-4">
            <div className="flex items-center gap-3">
              {state.status === 'complete' ? (
                <FileDown className="h-5 w-5 text-primary shrink-0" />
              ) : state.status === 'error' ? (
                <div className="h-5 w-5 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
                  <span className="text-destructive text-xs font-bold">!</span>
                </div>
              ) : (
                <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0" />
              )}
              <h3 className="font-semibold text-foreground">{state.title}</h3>
            </div>
            <Progress value={state.percent} className="h-2" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{state.message}</span>
              <span className="font-medium tabular-nums text-foreground">{state.percent}%</span>
            </div>
          </div>
        </div>
      )}
    </ExportProgressContext.Provider>
  );
}
